import * as stylex from "@stylexjs/stylex";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useId } from "react";
import { type Clock, formatClock, formatLength, timeParts } from "@/domain/time";
import "@/theme/tokens.stylex";
import { colors, motion, radii } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { Icon } from "./Icon";
import { press } from "./press";
type TimeFieldLabels = {
  noEnd: string;
  change: string;
};
type TimeFieldProps = {
  label: string;
  value: number | undefined;
  clock: Clock;
  length?: number;
  open: boolean;
  error?: string;
  labels: TimeFieldLabels;
  onToggle: () => void;
  compact?: boolean;
  off?: string;
  xstyle?: stylex.StyleXStyles;
};
const styles = {
  root: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x167g77z",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  button: {
    kAzted: "xbktkl8",
    kzqmXN: "xh8yej3",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "x1qughib",
    kOIVth: "x167g77z",
    k8WAf4: "xp59q4u",
    kE3dHu: "xzjhap9",
    kpe85a: "xnuq7ks",
    kB7OPa: "x9f619",
    kWkggS: "xh540kj",
    kMzoRj: "xmkeg23",
    ksu8eU: "x1y0btm7",
    kVAM5u: "x1ky62hi",
    kaIpWk: "x1fv5t7x",
    kGVxlE: "x1gnnqk1",
    kMv6JI: "x17jlq2w",
    kMwMTN: "x1a11ngh",
    kkrTdU: "x1ypdohk",
    k9WMMc: "xdpxx8g",
    k1ekBW: "x16n73rl",
    kIyJzY: "xlc9y7w",
    kAMwcw: "x1em5f5i",
    $$css: true
  },
  open: {
    kVAM5u: "xn35ayp",
    kGVxlE: "x1qf5l1u",
    $$css: true
  },
  invalid: {
    kVAM5u: "xnefgz8",
    $$css: true
  },
  disabled: {
    kWkggS: "xjbqb8w",
    kkrTdU: "xt0e3qv",
    $$css: true
  },
  invalidOpen: {
    kGVxlE: "x5cda23",
    $$css: true
  }
};
export function TimeField({
  label,
  value,
  clock,
  length,
  open,
  error,
  labels,
  onToggle,
  compact,
  off,
  xstyle
}: TimeFieldProps) {
  const errorId = useId();
  const invalid = Boolean(error);
  const reading = value === undefined ? {
    time: labels.noEnd
  } : timeParts(value, clock);
  const disabled = off !== undefined;
  const ariaLabel = disabled ? `${label}, ${off}` : `${label}, ${spokenTime(value, clock, labels.noEnd)}. ${labels.change}`;
  return <div {...stylex.props(styles.root, xstyle)}>
      <div className="x78zum5 x1qughib x1pha0wt x167g77z">
        <span className="x12feioo xbt1u0e xhivx12">{label}</span>
        {length !== undefined && <span className="xr0iwhn xhunfcg xt3et7w xtvhhri xuxw1ft">{formatLength(length)}</span>}
      </div>
      <button type="button" aria-expanded={open} aria-label={ariaLabel} aria-describedby={invalid ? errorId : undefined} disabled={disabled} onClick={onToggle} {...stylex.props(styles.button, open && styles.open, invalid && styles.invalid, invalid && open && styles.invalidOpen, disabled && styles.disabled, press.scale)}>
        <span {...{
        0: {
          className: "x78zum5 x1pha0wt x1a02dak x167g77z xeuugli"
        },
        1: {
          className: "x78zum5 x1pha0wt x1a02dak x167g77z xeuugli x5lhwog"
        }
      }[!!disabled << 0]}>
          <span {...{
          0: {
            className: "x1i1m3gp x1v94iar x1yrajzq xss6m8b x1u7k74"
          },
          2: {
            className: "x1v94iar x1yrajzq xss6m8b x1u7k74 x1q7jzdg"
          },
          1: {
            className: "x1i1m3gp x1yrajzq xss6m8b x1u7k74 x15i68ds"
          },
          3: {
            className: "x1yrajzq xss6m8b x1u7k74 x1q7jzdg x15i68ds"
          }
        }[!!compact << 1 | !!(value === undefined) << 0]}>
            {reading.time}
          </span>
          {reading.meridiem && <span className="xr0iwhn x1jvydc1 xbac8wx">{reading.meridiem}</span>}
        </span>
        <Icon icon={open ? ChevronUp : ChevronDown} size={20} />
      </button>
      {invalid && <span id={errorId} role="alert" className="xr0iwhn xhunfcg xt3et7w xtvhhri x3258oq">
          {error}
        </span>}
    </div>;
}
function spokenTime(value: number | undefined, clock: Clock, noEnd: string) {
  return value === undefined ? noEnd : formatClock(value, clock);
}